import { type StudyThreadSelectDto, StudyThreadSelectDtoKillChainCategoryEnum } from '@@types/api.types';
import { ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { Tile } from '@components/shared/tile.component';
import { successToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { useGetKillChains } from '@queries/kill-chain.query';
import { useUpdateMeiaMissionThread } from '@queries/meia.query';
import { getStudyThreadKillChainLabel, toCreateThreadKillChain, toUpdateThreadKillChain } from '@utils/meia-enum.util';
import {
  CompassIcon,
  CrosshairIcon,
  GitBranchIcon,
  RadioTowerIcon,
  SatelliteIcon,
  ShieldAlertIcon,
  ShieldIcon,
  TruckIcon,
  WrenchIcon,
} from 'lucide-react';
import { type FC, useMemo, useState } from 'react';

type MeiaThreadPatternStripProps = {
  canEdit: boolean;
  customStageCount: number;
  missionId: string;
  studyId: string;
  thread: StudyThreadSelectDto;
};

type PatternInfo = {
  icon: JSX.Element;
  /** Static context line; the phase sequence itself is derived from the seeded kill-chain data. */
  tagline: string;
};

const patternInfoByCategory: Record<StudyThreadSelectDtoKillChainCategoryEnum, PatternInfo> = {
  [StudyThreadSelectDtoKillChainCategoryEnum.C2]: {
    icon: <RadioTowerIcon className="size-3.5" />,
    tagline: 'Command-and-control decision cycle.',
  },
  [StudyThreadSelectDtoKillChainCategoryEnum.COUNTER_UAS]: {
    icon: <ShieldIcon className="size-3.5" />,
    tagline: 'Counter-UAS engagement chain.',
  },
  [StudyThreadSelectDtoKillChainCategoryEnum.CUSTOM]: {
    icon: <WrenchIcon className="size-3.5" />,
    tagline: 'User-defined kill chain. Add your own stages.',
  },
  [StudyThreadSelectDtoKillChainCategoryEnum.CYBER]: {
    icon: <ShieldAlertIcon className="size-3.5" />,
    tagline: 'Cyber engagement chain.',
  },
  [StudyThreadSelectDtoKillChainCategoryEnum.D3A]: {
    icon: <CompassIcon className="size-3.5" />,
    tagline: 'Army targeting cycle.',
  },
  [StudyThreadSelectDtoKillChainCategoryEnum.F2T2EA]: {
    icon: <CrosshairIcon className="size-3.5" />,
    tagline: 'Joint targeting kill chain.',
  },
  [StudyThreadSelectDtoKillChainCategoryEnum.ISR]: {
    icon: <SatelliteIcon className="size-3.5" />,
    tagline: 'Intelligence collection cycle (TCPED).',
  },
  [StudyThreadSelectDtoKillChainCategoryEnum.LOGISTICS]: {
    icon: <TruckIcon className="size-3.5" />,
    tagline: 'Sustainment chain.',
  },
};

const patternOrder: StudyThreadSelectDtoKillChainCategoryEnum[] = [
  StudyThreadSelectDtoKillChainCategoryEnum.F2T2EA,
  StudyThreadSelectDtoKillChainCategoryEnum.D3A,
  StudyThreadSelectDtoKillChainCategoryEnum.CYBER,
  StudyThreadSelectDtoKillChainCategoryEnum.ISR,
  StudyThreadSelectDtoKillChainCategoryEnum.LOGISTICS,
  StudyThreadSelectDtoKillChainCategoryEnum.C2,
  StudyThreadSelectDtoKillChainCategoryEnum.COUNTER_UAS,
  StudyThreadSelectDtoKillChainCategoryEnum.CUSTOM,
];

const formatStageCount = (params: {
  category: StudyThreadSelectDtoKillChainCategoryEnum;
  customStageCount: number;
  phaseNames?: string[];
}): string => {
  const { category, customStageCount, phaseNames } = params;
  if (category === StudyThreadSelectDtoKillChainCategoryEnum.CUSTOM) return `${customStageCount} custom`;
  if (!phaseNames) return '… stages';
  return `${phaseNames.length} stages`;
};

export const MeiaThreadPatternStrip: FC<MeiaThreadPatternStripProps> = (props) => {
  const { canEdit, customStageCount, missionId, studyId, thread } = props;
  const [pendingCategory, setPendingCategory] = useState<StudyThreadSelectDtoKillChainCategoryEnum | null>(null);
  const { isPending: isSwitching, mutateAsync: updateThread } = useUpdateMeiaMissionThread();
  const { data: killChains = [] } = useGetKillChains();

  // Phase names per category, in seeded order (the endpoint sorts by category, then phase order).
  // These are the same rows the backend copies into swimlanes when a thread is (re)seeded, so
  // counts and sequences shown here always match what seeding produces.
  const phaseNamesByCategory = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const killChain of killChains) {
      const names = map.get(killChain.category) ?? [];
      names.push(killChain.name);
      map.set(killChain.category, names);
    }
    return map;
  }, [killChains]);

  const activeInfo = patternInfoByCategory[thread.killChainCategory];
  const activePhaseNames = phaseNamesByCategory.get(thread.killChainCategory);
  const activeDescription = activePhaseNames?.length
    ? `${activePhaseNames.join('–')}. ${activeInfo.tagline}`
    : activeInfo.tagline;

  const handleConfirmSwitch = async (): Promise<void> => {
    if (!pendingCategory) return;
    await updateThread({
      killChainCategory: toUpdateThreadKillChain(toCreateThreadKillChain(pendingCategory)),
      missionId,
      name: thread.name,
      narrative: thread.narrative,
      role: thread.role,
      studyId,
      threadId: thread.id,
    });
    successToast({ description: 'Thread pattern switched and stages reseeded.' });
    setPendingCategory(null);
  };

  return (
    <>
      <Tile
        contentClassName="gap-3"
        header={{
          title: (
            <span className="inline-flex items-center gap-2">
              <GitBranchIcon className="size-4 text-primary" />
              <span>Thread Pattern</span>
            </span>
          ),
        }}
      >
        <div className="flex flex-wrap gap-2">
          {patternOrder.map((category) => {
            const info = patternInfoByCategory[category];
            const isActive = category === thread.killChainCategory;

            return (
              <button
                key={category}
                className={cn(
                  'flex w-[104px] flex-col gap-1 rounded-md border px-2.5 py-2 text-left transition-colors',
                  isActive ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-gray-200',
                  canEdit && !isActive && 'cursor-pointer hover:border-primary/40',
                  !canEdit && !isActive && 'cursor-default',
                )}
                disabled={isSwitching}
                onClick={() => {
                  if (!canEdit || isActive) return;
                  setPendingCategory(category);
                }}
                type="button"
              >
                <span className="flex items-center gap-1.5">
                  <span className={cn('shrink-0', isActive ? 'text-primary' : 'text-gray-500')}>{info.icon}</span>
                  <span className={cn('text-xs font-semibold', isActive ? 'text-gray-950' : 'text-gray-800')}>
                    {getStudyThreadKillChainLabel(category)}
                  </span>
                </span>
                <span className="text-[10px] leading-tight text-gray-500">
                  {formatStageCount({ category, customStageCount, phaseNames: phaseNamesByCategory.get(category) })}
                </span>
              </button>
            );
          })}
        </div>

        <p className="text-xs text-gray-600">
          <span className="font-medium text-gray-700">{getStudyThreadKillChainLabel(thread.killChainCategory)}</span> ·{' '}
          {activeDescription}
        </p>
      </Tile>

      <ConfirmationModal
        confirmLabel="Switch pattern"
        description="Switching the thread pattern reseeds its stages from the new template. Existing stage content for this thread will be replaced."
        isOpen={Boolean(pendingCategory)}
        isPending={isSwitching}
        onConfirm={handleConfirmSwitch}
        setIsOpen={(isOpen) => {
          if (!isOpen) setPendingCategory(null);
        }}
        title="Switch thread pattern?"
        variant="destructive"
      />
    </>
  );
};
